import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  RoleBadge,
  ToggleButton,
  ToggleButtonGroup,
} from '@cere/cere-design-system';
import type { Member, Scope, ScopeGrants } from '@cef-ai/vault-sdk';
import { grantsFromLevels, saveOrgMemberGrants, type LevelChoice } from '@/lib/clients/vault/org-members';
import { toDisplayError } from '@/lib/vault/step-up-error';
import { useVaultSession } from '@/src/state/session';
import '@/src/styles/orgs.css';

/**
 * A roster row whose grants are DISCLOSED to this caller — the only kind the
 * editor may run against. The editor seeds its controls from `scopes`, so an
 * undisclosed row (absent, not `{}`) collapsed in here would seed as
 * "holds nothing" and a save would REVOKE grants the caller never saw.
 */
export type DisclosedMember = Member & { scopes: ScopeGrants };

const LEVELS: LevelChoice[] = ['none', 'audit', 'reader', 'member'];

const LEVEL_LABEL: Record<LevelChoice, string> = {
  none: 'None',
  audit: 'Audit',
  reader: 'Reader',
  member: 'Member',
};

interface Props {
  open: boolean;
  /** The vault the section is administering — the caller's OWN vault id on
   *  the My-vault selection (the owner edits that roster like any org's),
   *  the selected org's id otherwise. */
  vaultId: string;
  /** The roster row being edited. Callers mount one dialog per row (`key`),
   *  because the level state seeds from the row on mount. */
  member: DisclosedMember;
  /** The org vault's scopes — the grant tree this editor assigns into. */
  orgScopes: Scope[];
  /** Fired once the write commits, so the section can re-read the roster. */
  onSaved: () => void;
  onClose: () => void;
}

/**
 * Replace one member's FULL scope-grant set on the selected org:
 * `memberships.putScopes(vaultId, pubkey, scopes, { standing })`, signed
 * inside the save click. Saving is never a merge — the server deletes then
 * reinserts, so "None" on a row is how a grant is revoked and an all-None
 * save is the real write "this member holds nothing". The standing half of
 * the signed document travels off the roster row unchanged (role always,
 * `expiresAt` exactly as spelled there, both or neither).
 */
export function EditMemberGrantsDialog({ open, vaultId, member, orgScopes, onSaved, onClose }: Props) {
  const session = useVaultSession();
  const [levels, setLevels] = useState<Record<string, LevelChoice>>(() =>
    Object.fromEntries(orgScopes.map((s) => [s.name, member.scopes[s.name] ?? 'none'])));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setLevel(name: string, choice: LevelChoice) {
    setLevels((ls) => ({ ...ls, [name]: choice }));
  }

  async function handleSave() {
    if (orgScopes.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      // Same discipline as AddMemberDialog: the handle (and any step-up
      // ceremony it costs) resolves inside this click, warming the signing
      // SDK's keys before the grant-document-bearing write runs.
      await session.signingHandle();
      await saveOrgMemberGrants(session.sdk, vaultId, member, grantsFromLevels(levels));
      onSaved();
      onClose();
    } catch (e) {
      setError(toDisplayError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      title="Edit grants"
      onClose={() => !submitting && onClose()}
      isLoading={submitting}
      customActions={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" disabled={orgScopes.length === 0 || submitting} onClick={() => { void handleSave(); }}>
            {submitting ? 'Saving…' : 'Save grants'}
          </Button>
        </>
      }
    >
      <Box className="vault-orgs__wizard-body">
        <div className="vault-orgs__standing">
          <RoleBadge label={member.role === 'admin' ? 'Admin' : 'Member'} />
          <span className="vault-orgs__standing-text">
            {member.expiresAt !== undefined
              ? `expires ${member.expiresAt}`
              : 'no expiry'}
          </span>
        </div>
        <p className="vault-orgs__wizard-sub">
          Saving replaces this member’s full grant set — “None” revokes that grant.
          Their role and expiry are signed unchanged from the roster row.
        </p>
        {orgScopes.length === 0 ? (
          <p className="vault-orgs__wizard-sub" data-testid="orgs-grants-empty">
            This organization has no scopes to grant yet.
          </p>
        ) : (
          orgScopes.map((s) => (
            <div key={s.name} className="vault-orgs__grant-row">
              <span className="vault-orgs__grant-name" title={s.name}>{s.displayName ?? s.name}</span>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={levels[s.name] ?? 'none'}
                onChange={(_, value) => { if (value !== null) setLevel(s.name, value as LevelChoice); }}
              >
                {LEVELS.map((level) => (
                  <ToggleButton key={level} value={level}>{LEVEL_LABEL[level]}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </div>
          ))
        )}
        {error && <Alert severity="error" className="vault-orgs__wizard-error">{error}</Alert>}
      </Box>
    </Dialog>
  );
}
